import { useState, useCallback, useContext, useReducer, createContext, useEffect, useRef, useMemo } from 'react';
import { supabase } from 'supabaseClient';
import AppContext from 'AppContext';
import { handlers } from 'pages/dashboard/challenges/state/handlers';
import { reducer } from 'pages/dashboard/challenges/state/reducers';
import { initialState } from 'pages/dashboard/challenges/state/initialState';
import { useTeam } from 'contexts/TeamContext';
import { ACTIONS } from 'pages/dashboard/challenges/state/constants';

const CHALLENGE_STATUS = {
  NEW: 'new',
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived'
};

const ChallengesStateContext = createContext();

export const ChallengesStateProvider = ({ children }) => {
  const { platformUser } = useContext(AppContext);
  const { currentTeamId, loading: teamLoading } = useTeam();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false);

  // Create handlers with current state and team ID
  const challengeHandlers = useMemo(() => 
    handlers(dispatch, currentTeamId),
    [dispatch, currentTeamId]
  );

  // Add memoized selectors
  const getInstancesByCategory = useCallback((categoryId) => {
    // console.log('getInstancesByCategory called with:', categoryId);
    // console.log('Current state:', state.challenges);
    const category = state.challenges.categories[categoryId];
    if (!category) return [];

    // Get all instances for this category
    const instances = Object.values(state.challenges.instances)
      .filter(instance => instance.category_id === categoryId);
    // console.log('Category instances:', instances);
    return instances;
  }, [state.challenges.instances, state.challenges.categories]);

  const getAttachedDraft = useCallback((categoryId, instanceId) => {
    const instance = state.challenges.instances[instanceId];
    if (!instance?.draft_id || instance.status !== CHALLENGE_STATUS.ACTIVE) return null;
    
    const category = state.challenges.categories[categoryId];
    return category?.challengeDrafts?.[instance.draft_id] || null;
  }, [state.challenges.instances, state.challenges.categories]);

  const getCategory = useCallback((categoryId) => {
    return state.challenges.categories[categoryId];
  }, [state.challenges.categories]);

  const getInstance = useCallback((instanceId) => {
    return state.challenges.instances[instanceId];
  }, [state.challenges.instances]);

  const fetchChallenges = useCallback(async () => {
    // Don't do anything while team is loading
    if (teamLoading) {
      return;
    }
    
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current || loading) {
      // console.log('Already fetching challenges, skipping...');
      return;
    }

    if (!currentTeamId) {
      // console.log('No team ID available, skipping fetch...');
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      const { error: fetchError } = await challengeHandlers.fetchChallenges();
      
      if (fetchError) {
        setError(fetchError);
        console.error("Error fetching challenges:", fetchError);
      }
    } catch (err) {
      console.error("Unexpected error fetching challenges:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [challengeHandlers, currentTeamId, teamLoading]);

  // Only fetch when platformUser changes and exists
  useEffect(() => {
    if (!platformUser) return; // Early return if no user

    // console.log('Fetching challenges for user:', platformUser.id);
    fetchChallenges();

    // No cleanup needed since we're using refs for fetch state
  }, [platformUser?.id, fetchChallenges]);

  const addChallengeInstance = useCallback(async (instance) => {
    if (!platformUser) {
      console.error("User not authenticated");
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      const { error: addError } = await challengeHandlers.addChallengeInstance(instance);
      
      if (addError) {
        setError(addError);
        console.error("Error adding challenge:", addError);
      }
    } catch (err) {
      console.error("Unexpected error adding challenge:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [platformUser, challengeHandlers]);

  const updateChallengeInstance = useCallback(async (challengeId, updates) => {
    if (!platformUser) {
      console.error("User not authenticated");
      return;
    }
    
    // console.log('updateChallengeInstance called with:', { challengeId, updates });
    setLoading(true);
    setError(null);

    try {
      const { error: updateError, data } = await challengeHandlers.updateChallengeInstance(challengeId, updates);
      // console.log('Update response:', { error: updateError, data });
      
      if (updateError) {
        setError(updateError);
        return { error: updateError };
      }

      // Get current instance
      const currentInstance = state.challenges.instances[challengeId];
      
      // Force a state update by dispatching directly
      dispatch({
        type: ACTIONS.UPDATE_CHALLENGE_INSTANCE,
        payload: {
          instance: {
            ...currentInstance,
            ...updates,
            id: challengeId,
            category_id: updates.category_id || currentInstance?.category_id,
            // Keep is_active true for visibility in list
            is_active: true,
            // Use provided status or keep current, default to NEW for new instances
            status: updates.status || currentInstance?.status || CHALLENGE_STATUS.NEW
          },
          category_id: updates.category_id || currentInstance?.category_id
        }
      });

      // Log state after dispatch
      // console.log('State after dispatch:', state.challenges);
      return { data, error: null };
    } catch (err) {
      // console.error('Error in updateChallengeInstance:', err);
      return { error: err };
    } finally {
      setLoading(false);
    }
  }, [platformUser, challengeHandlers, dispatch, state.challenges.instances]);

  const archiveChallengeInstance = useCallback(async (challengeId) => {
    if (!platformUser) {
      // console.error("User not authenticated");
      return;
    }
    
    setLoading(true);
    setError(null);

    const { error: archiveError } = await challengeHandlers.archiveChallengeInstance(challengeId);
    
    if (archiveError) {
      setError(archiveError);
    }
    
    setLoading(false);
  }, [platformUser, challengeHandlers]);

  const addChallengeDraft = useCallback(async (categoryId, draft) => {
    // console.log('addChallengeDraft called with:', { categoryId, draft });
    // Ensure proper status and is_active flags
    const draftWithDefaults = {
      ...draft,
      status: CHALLENGE_STATUS.DRAFT,
      is_active: draft.is_active || false
    };
    const result = await challengeHandlers.addChallengeDraft(categoryId, draftWithDefaults);
    // console.log('Add draft result:', result);
    // console.log('State after adding draft:', state.challenges);
    return result;
  }, [challengeHandlers, state.challenges]);

  const value = {
    state,
    loading,
    error,
    // Add selectors to context value
    getInstancesByCategory,
    getAttachedDraft,
    getCategory,
    getInstance,
    // Existing handlers
    fetchChallenges,
    addChallengeInstance,
    updateChallengeInstance,
    archiveChallengeInstance,
    attachCategory: challengeHandlers.attachCategory,
    detachCategory: challengeHandlers.detachCategory,
    addChallengeDraft,
    deleteDraft: challengeHandlers.deleteDraft,
    attachChallenge: challengeHandlers.attachChallenge,
    detachChallenge: challengeHandlers.detachChallenge
  };

  return (
    <ChallengesStateContext.Provider value={value}>
      {children}
    </ChallengesStateContext.Provider>
  );
};

export const useChallengesState = () => {
  const context = useContext(ChallengesStateContext);
  if (!context) {
    throw new Error('useChallengesState must be used within a ChallengesStateProvider');
  }
  return context;
}; 